import useMobileDetect from "@/hooks/useMobileDetect";
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  message as messageToast,
} from "antd";
import { CloseSmall } from "@icon-park/react";
import { CookieType } from "@/service/user/enum";
import { useState } from "react";
import { cookieRecord } from "@/service/user";
import { Storage } from "@/utils/storage";
import "./style/Cookie.css";

export type ManageCookieProps = {
  open: boolean;
  onClose: () => void;
  onManageCookie: () => void;
};

const options = [
  {
    label: {
      title: CookieType.Required,
      content:
        "These cookies are necessary for the site to function properly, including capabilities like logging in and adding items to the cart.",
    },
    value: CookieType.Required,
    disabled: true,
  },
  {
    label: {
      title: CookieType.Personalization,
      content:
        "These cookies store details about your actions to personalize your next visit to the website.",
    },
    value: CookieType.Personalization,
  },
  {
    label: {
      title: CookieType.Marketing,
      content:
        "These cookies are used to optimize marketing communications and show you ads on other sites",
    },
    value: CookieType.Marketing,
  },
  {
    label: {
      title: CookieType.Analytics,
      content:
        "These cookies help us understand how you interact with the site. We use this data to identify areas to improve.",
    },
    value: CookieType.Analytics,
  },
];

export const ManageCookie: React.FC<ManageCookieProps> = ({
  open,
  onClose,
  onManageCookie,
  ...props
}) => {
  const isMobile = useMobileDetect();
  const [selected, setSelected] = useState<string[]>([
    CookieType.Required,
    CookieType.Personalization,
    CookieType.Marketing,
    CookieType.Analytics,
  ]);

  const handleCookie = async (value: string[]) => {
    const { status, message } = await cookieRecord(
      localStorage.getItem("deviceId") ?? "",
      { accepted: value },
    );

    if (status === 200) {
      onClose();
      onManageCookie();
      Storage.setItem("cookie-consent", { accepted: value });
    } else {
      messageToast.error(message);
    }
  };

  return (
    <Drawer
      className="cookieDrawer"
      title={
        <div className="text-xl leading-26 font-bold text-bravve-100">
          Cookie preferences
        </div>
      }
      open={open}
      onClose={onClose}
      closeIcon={<CloseSmall theme="filled" fill="#402C22" size={"2.8rem"} />}
      placement={isMobile ? "bottom" : "right"}
      height={isMobile ? "86.2%" : 0}
      width={isMobile ? 0 : "37.5%"}
      styles={{
        header: {
          padding: isMobile ? "1.6rem" : "1.6rem 2.4rem",
        },
        body: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: isMobile ? "0.8rem 1.6rem 2.4rem" : "0.8rem 2.4rem 2.4rem",
        },
      }}
    >
      <div>
        <div>
          <div className="text-lg font-medium text-bravve-100 leading-24">You control data</div>
          <div className="mt-8 text-bravve-sub-100 leading-22">
            Learn more about the cookies we use, and choose which cookies to
            allow.
          </div>
          <Divider />
        </div>
        <Checkbox.Group
          defaultValue={[
            CookieType.Required,
            CookieType.Personalization,
            CookieType.Marketing,
            CookieType.Analytics,
          ]}
          onChange={(value) => {
            setSelected(value);
          }}
        >
          {options.map(({ label, value, disabled }) => {
            return (
              <div
                key={value}
                className="mt-24 flex w-full flex-col flex-wrap first:mt-0"
              >
                <Checkbox value={value} disabled={disabled} className="w-20">
                  <div className="pl-8 text-lg font-medium text-bravve-100 leading-24">
                    {label.title}
                  </div>
                </Checkbox>
                <div className="ml-36 text-bravve-sub-100 leading-22">{label.content}</div>
              </div>
            );
          })}
        </Checkbox.Group>
      </div>

      <div className="mb-4 mt-24">
        <div className="flex w-full justify-between">
          <Button
            className="w-full"
            size="small"
            onClick={() => {
              handleCookie([]);
            }}
          >
            Reject all
          </Button>
          <Button
            className="ml-10 w-full lg:ml-12"
            size="small"
            onClick={() => {
              handleCookie(selected);
            }}
          >
            Save my choies
          </Button>
        </div>
        <Button
          type="primary"
          className="mt-12 w-full"
          onClick={() => {
            handleCookie([
              CookieType.Required,
              CookieType.Personalization,
              CookieType.Marketing,
              CookieType.Analytics,
            ]);
          }}
        >
          Accept all
        </Button>
      </div>
    </Drawer>
  );
};
